import {
  LayoutDashboard,
  Package,
  ClipboardList,
  Users,
  UsersRound,
  BarChart3,
  Settings,
  Clock,
  CheckCircle2,
  Truck,
  PackageCheck,
  AlertCircle,
  ShieldAlert,
  Calendar,
  AlertTriangle,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useAdminStatus } from "@/hooks/useAdminStatus";

interface PackageCounts {
  pendingReview: number;
  awaitingPickupConfirmation: number;
  pickupScheduled: number;
  pickedUp: number;
  delivered: number;
}

interface RoleBasedSidebarProps {
  packageCounts: PackageCounts;
}

// Admin-only navigation items (exceptions & overrides focus)
const adminNavItems = [
  { title: "Dashboard", url: "/admin", icon: LayoutDashboard },
  { title: "All Packages", url: "/admin/packages", icon: Package },
  { title: "My Tasks", url: "/admin/tasks", icon: ClipboardList },
  { title: "Team Roles", url: "/admin/team", icon: UsersRound },
  { title: "Users", url: "/admin/users", icon: Users },
  { title: "Analytics", url: "/admin/analytics", icon: BarChart3 },
  { title: "Settings", url: "/admin/settings", icon: Settings },
];

// Logistics Manager navigation items (same as admin - full visibility)
const managerNavItems = [
  { title: "Dashboard", url: "/admin", icon: LayoutDashboard },
  { title: "All Packages", url: "/admin/packages", icon: Package },
  { title: "My Tasks", url: "/admin/tasks", icon: ClipboardList },
  { title: "Team Roles", url: "/admin/team", icon: UsersRound },
  { title: "Users", url: "/admin/users", icon: Users },
  { title: "Analytics", url: "/admin/analytics", icon: BarChart3 },
  { title: "Settings", url: "/admin/settings", icon: Settings },
];

// Admin status items (exceptions view)
const adminStatusItems = [
  {
    label: "Pending Review",
    key: "pendingReview" as const,
    icon: AlertCircle,
    color: "text-amber-500",
  },
  {
    label: "Awaiting Pickup",
    key: "awaitingPickupConfirmation" as const,
    icon: Clock,
    color: "text-blue-500",
  },
  {
    label: "Pickup Scheduled",
    key: "pickupScheduled" as const,
    icon: Truck,
    color: "text-purple-500",
  },
  {
    label: "Picked Up",
    key: "pickedUp" as const,
    icon: PackageCheck,
    color: "text-cyan-500",
  },
  {
    label: "Delivered / Returned",
    key: "delivered" as const,
    icon: CheckCircle2,
    color: "text-emerald-500",
  },
];

// Manager status items (execution view)
const managerStatusItems = [
  {
    label: "Pending Review",
    key: "pendingReview" as const,
    icon: AlertCircle,
    color: "text-amber-500",
  },
  {
    label: "Awaiting Pickup",
    key: "awaitingPickupConfirmation" as const,
    icon: Clock,
    color: "text-blue-500",
  },
  {
    label: "Pickup Scheduled",
    key: "pickupScheduled" as const,
    icon: Truck,
    color: "text-purple-500",
  },
  {
    label: "Picked Up",
    key: "pickedUp" as const,
    icon: PackageCheck,
    color: "text-cyan-500",
  },
  {
    label: "Delivered / Returned",
    key: "delivered" as const,
    icon: CheckCircle2,
    color: "text-emerald-500",
  },
];

export const RoleBasedSidebar = ({ packageCounts }: RoleBasedSidebarProps) => {
  const { state } = useSidebar();
  const { isAdmin } = useAdminStatus();
  const isCollapsed = state === "collapsed";

  // Select navigation and status items based on role
  const navItems = isAdmin ? adminNavItems : managerNavItems;
  const statusItems = isAdmin ? adminStatusItems : managerStatusItems;
  const footerTitle = isAdmin ? "Package Overview" : "Package Overview";

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarContent className="pt-4">
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground px-3">
            {isAdmin ? "Admin" : "Logistics"}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <NavLink
                      to={item.url}
                      end={item.url === "/admin"}
                      className="flex items-center gap-3 px-3 py-2 rounded-md transition-colors hover:bg-muted/50"
                      activeClassName="bg-primary/10 text-primary font-medium"
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      <span className="truncate">{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Status Overview Footer */}
      <SidebarFooter className="border-t p-4">
        <div className={cn("space-y-3", isCollapsed && "hidden")}>
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            {footerTitle}
          </h4>
          <div className="space-y-2">
            {statusItems.map((item) => {
              // Handle both packageCounts keys and potential exception counts
              const count = item.key in packageCounts 
                ? packageCounts[item.key as keyof PackageCounts] 
                : 0;
              
              return (
                <div
                  key={item.label}
                  className="flex items-center justify-between text-sm"
                >
                  <div className="flex items-center gap-2">
                    <item.icon className={cn("h-4 w-4", item.color)} />
                    <span className="text-muted-foreground truncate text-xs">
                      {item.label}
                    </span>
                  </div>
                  <span className="font-semibold tabular-nums">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Collapsed state - just show total */}
        {isCollapsed && (
          <div className="flex flex-col items-center gap-1">
            {isAdmin ? (
              <ShieldAlert className="h-5 w-5 text-muted-foreground" />
            ) : (
              <Package className="h-5 w-5 text-muted-foreground" />
            )}
            <span className="text-xs font-semibold">
              {Object.values(packageCounts).reduce((a, b) => a + b, 0)}
            </span>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
};
